<template>
  <section>
    <label v-if="label" for="inviteMembers" class="formkit-label block mb-1 font-bold text-sm">{{ label }}</label>
    <ul :class="props.classList">
      <li
        v-for="item in chips"
        :key="item"
        class="flex gap-1 p-1 rounded bg-slate-200 dark:bg-zinc-700"
        :class="getItemClass(item)"
        @animationend="duplicateAnimation = undefined"
      >
        <div class="text-slate-600 dark:text-zinc-300">
          <div>
            <span data-testid="chips-item">{{ item }}</span>
          </div>
        </div>
        <div v-if="!disabledInModes(['readonly'])" class="text-zinc-600 dark:text-zinc-400">
          <Icon name="general.closeCircle" size="base" class="cursor-pointer" @click.prevent.stop="deleteChip(item)" data-testid="chips-cancel" />
        </div>
      </li>
      <li class="flex grow items-center" v-if="!disabledInModes(['readonly', 'readAndDelete'])">
        <input
          type="text"
          id="inviteMembers"
          v-model="currentValue"
          :placeholder="placeholder"
          :pattern="pattern"
          @keyup="onKeyUp"
          @blur="addChip"
          @keypress.enter="addChip"
          @keypress.space="addChip"
          @keydown.delete="deleteLast"
          class="w-full bg-transparent dark:text-zinc-300 focus:outline-none border-none outline-none focus:border-transparent focus:ring-0"
        />
        <Icon v-if="suffixIcon" :name="suffixIcon" size="base" class="suffix-icon" @click.prevent.stop="onSubmit" data-testid="submit" />
      </li>
    </ul>
  </section>
</template>

<script lang="ts" setup>
import { ref, Ref, toRaw, watch } from 'vue';
import Icon from './../../icon/Icon.vue';

const ERROR_CLASS = 'border bg-red-100 dark:bg-red-700 border-red-300 dark:border-red-900';

type ItemClass = {
  [key: string]: boolean;
};

interface Props {
  mode?: 'readonly' | 'readAndDelete';
  classList?: string;
  modelValue?: string[];
  label?: string;
  placeholder?: string;
  errors?: string[];
  delimiters?: string[];
  pattern?: string;
  suffixIcon?: string;
}

const props = withDefaults(defineProps<Props>(), {
  delimiters: () => [',', ';', ' '],
  classList: () =>
    'flex flex-wrap items-center justify-start gap-1 p-2 border border-slate-300 dark:border-zinc-700 rounded-10 focus-within:ring focus-within:border-blue-500 focus:ring focus:ring-blue-500/50',
});

const emit = defineEmits(['update:modelValue', 'error', 'submit']);
const chips: Ref<string[]> = props.modelValue ? ref(toRaw(props.modelValue)) : ref([]);
const currentValue = ref('');

const duplicateAnimation: Ref<string | undefined> = ref();

const getItemClass = (item: string) => {
  const css: ItemClass = {};

  css[ERROR_CLASS] = (props.errors && props.errors.includes(item)) || false;
  css['flashing'] = item === duplicateAnimation.value;

  return css;
};

const onKeyUp = (e: KeyboardEvent) => {
  if (props.delimiters.includes(e.key)) {
    currentValue.value = currentValue.value.slice(0, -1);
    addChip();
  }
};
const addChip = () => {
  const value = currentValue.value.trim();

  if (value.length === 0) {
    return;
  }

  if (props.pattern) {
    const pattern = new RegExp(props.pattern, 'i');

    if (!pattern.test(value)) {
      return emit('error', value);
    }
  }

  if (!chips.value.includes(value)) {
    chips.value.push(value);
    emit('update:modelValue', chips.value);
  } else {
    duplicateAnimation.value = currentValue.value;
  }

  currentValue.value = '';
};

const deleteLast = () => {
  if (currentValue.value.length > 0) {
    return;
  }

  if (chips.value.length > 0) {
    chips.value.splice(chips.value.length - 1, 1);
    emit('update:modelValue', chips.value);
  }
};

const deleteChip = (name: string) => {
  if (chips.value.includes(name)) {
    chips.value.splice(
      chips.value.findIndex(n => n === name),
      1
    );
    emit('update:modelValue', chips.value);
  }
};

const disabledInModes = (modes: string | string[]) => {
  if (!props.mode) {
    return false;
  }

  if (typeof modes === 'string') {
    return props.mode === modes;
  }

  return modes.indexOf(props.mode) > -1;
};

const onSubmit = () => {
  emit('submit', chips.value);
  currentValue.value = '';
};

watch(
  () => props.modelValue,
  value => {
    chips.value = value ?? [];
  }
);
</script>

<style scoped>
.suffix-icon {
  @apply w-5 h-5 cursor-pointer;
}
</style>

<style>
.suffix-icon > svg {
  @apply w-full h-full;
}
.flashing {
  animation: flash 0.5s;
}

@keyframes flash {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 50%;
  }
  100% {
    opacity: 1;
  }
}
</style>
